import Card from "components/Card/Card.tsx";
import { TextInput } from "components/inputs";
import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { ClaimsDataType } from "types/DataTypes.ts";

import {
  ControlledDateInput,
  ControlledInput,
} from "../../components/Card/ControlledInput";

type ClaimsTPOCCardProp = {
  claim: ClaimsDataType;
  onSave: (field: string, value: string | number) => void;
};

const getArray = (n: number) => [...Array(n).keys()].map((x) => x + 1);

const ClaimsTPOCCard = ({ claim, onSave }: ClaimsTPOCCardProp) => {
  const [displayIndex, setDispalyIndex] = useState(0);
  const {
    control,
    reset,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    if (!displayIndex) {
      const index = [5, 4, 3, 2, 1].find(
        (i) =>
          claim?.[`tpoc_date_${i}`] ||
          claim?.[`tpoc_amount_${i}`] ||
          claim?.[`funding_delayed_beyond_tpoc_start_date_${i}`],
      );
      if (index) {
        setDispalyIndex(index);
      }
    }
  }, [claim, displayIndex]);

  useEffect(() => {
    if (claim?.id) {
      const fields = Array.from({ length: 5 }, (_, i) => i + 1).reduce(
        (acc, index) => {
          acc[`tpoc_date_${index}`] = claim[`tpoc_date_${index}`] || "";
          acc[`tpoc_amount_${index}`] = claim[`tpoc_amount_${index}`] || "";
          acc[`funding_delayed_beyond_tpoc_start_date_${index}`] =
            claim[`funding_delayed_beyond_tpoc_start_date_${index}`] || "";
          return acc;
        },
        {},
      );

      reset(fields);
    }
  }, [claim, reset]);

  const handleFieldSave = useCallback(
    async (field: string, value: string | number) => {
      await onSave(field, value);
    },
    [onSave],
  );

  const renderGroup = useCallback(
    (index: number) => {
      return (
        <>
          <ControlledDateInput
            control={control}
            key={`tpoc_date_${index}`}
            label={`TPOC Date ${index}`}
            name={`tpoc_date_${index}`}
            errors={errors}
            onSave={(value) => handleFieldSave(`tpoc_date_${index}`, value)}
          />

          <ControlledInput
            control={control}
            key={`tpoc_amount_${index}`}
            label={`TPOC Amount ${index}`}
            name={`tpoc_amount_${index}`}
            errors={errors}
            onSave={(value) => handleFieldSave(`tpoc_amount_${index}`, value)}
            componentType={TextInput}
            maxLength={40}
            mask="none"
          />

          <ControlledDateInput
            control={control}
            key={`funding_delayed_beyond_tpoc_start_date_${index}`}
            label={`Funding Delayed Until Date ${index}`}
            name={`funding_delayed_beyond_tpoc_start_date_${index}`}
            errors={errors}
            onSave={(value) =>
              handleFieldSave(
                `funding_delayed_beyond_tpoc_start_date_${index}`,
                value,
              )
            }
          />
        </>
      );
    },
    [control, errors],
  );

  return !claim?.id ? null : (
    <Card
      heading="Total payment obligations to the claimant (TPOCS)"
      headerButton={{
        hasButton: true,
        children: "add_circle",
        size: "small",
        type: "button",
        onClick: () => {
          displayIndex > 4 ? null : setDispalyIndex(displayIndex + 1);
        },
      }}
    >
      {getArray(displayIndex).map((n) => renderGroup(n))}
    </Card>
  );
};

export default ClaimsTPOCCard;
